import { useEffect, useState } from 'react';
import { useOutletContext, useLocation } from 'react-router';
import { useQuery, useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import type {
  ProjectDto,
  InquiryDto,
  OfferDto,
  PaymentPlanDto,
  PaymentPlanItemDto,
  PaymentMode,
  PayoutStatus,
  PaymentPlanStatus,
  FundingIntentDto,
  Currency,
  ConfirmDirectPaymentDto,
  PaymentConfirmationDto,
  PaymentPlanItemStatus,
  DeclareOffPlatformPaymentDto,
  OffPlatformPaymentDto,
} from '@paladin-monorepo/common';
import axios from 'axios';
import {
  pickFirstEscrowItemIdNeedingFunding,
  pickFirstDirectItemIdAwaitingConfirmedReceipt,
  PaymentMethod,
  PaymentConfirmationStatus,
  ProjectWorkspaceFileCategory,
  PaymentPlanItemStatus as PaymentPlanItemStatusConst,
  PaymentMode as PaymentModeConst,
  OffPlatformPaymentStatus,
  PaymentPlanItemType,
  InvoiceStatus,
  DisputeReasonCode as DisputeReasonCodes,
  type DisputeReasonCode,
  buildPaymentProofDocumentUrl,
  DisputeStatus,
  FundingStatus,
  RefundRequestCategory,
  RefundRequestStatus,
  type RefundRequestDto,
} from '@paladin-monorepo/common';
import { useAuth } from '@/lib/auth';
import { paymentsApi } from '@/lib/api/payments';
import { projectsApi } from '@/lib/api/projects';
import { parseProblemDetailsDetail } from '@/utils/parseProblemDetailsDetail';
import { formatDate } from '@/utils/formatDate';
import { getAppFormatLocale } from '@/utils/locale';
import { Loading } from '@/components/Loading';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const itemStatusVariant: Record<PaymentPlanItemStatus, BadgeProps['variant']> = {
  PENDING: 'secondary',
  COMPLETED_PENDING_APPROVAL: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'outline',
};

const fundingStatusVariant: Record<FundingStatus, BadgeProps['variant']> = {
  UNFUNDED: 'outline',
  PARTIALLY_FUNDED: 'warning',
  FULLY_FUNDED: 'success',
  NOT_APPLICABLE: 'secondary',
};

const payoutStatusVariant: Record<PayoutStatus, BadgeProps['variant']> = {
  NOT_DUE: 'secondary',
  READY_FOR_PAYOUT: 'info',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  BLOCKED: 'error',
};

const modeVariant: Record<PaymentMode, BadgeProps['variant']> = {
  ESCROW: 'primaryDark',
  DIRECT: 'primaryLight',
  OFF_PLATFORM: 'secondaryLight',
};

const planStatusVariant: Record<PaymentPlanStatus, BadgeProps['variant']> = {
  ACTIVE: 'success',
  COMPLETED: 'info',
  CANCELLED: 'outline',
};

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat(getAppFormatLocale(), { style: 'currency', currency }).format(amount);
}

function escrowFundingErrorMessage(error: unknown, t: (key: string) => string): string {
  if (!axios.isAxiosError(error) || !error.response) {
    return t('workspace.payments.fundEscrow.error');
  }
  const { status, data } = error.response;
  const detailRaw = typeof data === 'object' && data && 'detail' in data ? String((data as { detail?: unknown }).detail) : undefined;
  const serverMsg = parseProblemDetailsDetail(detailRaw);

  if (status === 409) {
    if (serverMsg?.includes('No escrow payment plan item requires funding')) {
      return t('workspace.payments.fundEscrow.errorConflictNoFundingNeeded');
    }
    if (serverMsg?.includes('Escrow account has no linked provider wallet')) {
      return t('workspace.payments.fundEscrow.errorConflictEscrowWallet');
    }
    if (serverMsg?.includes('is not in ESCROW mode')) {
      return t('workspace.payments.fundEscrow.errorConflictItemNotEscrow');
    }
    if (serverMsg?.includes('cannot be funded')) {
      return t('workspace.payments.fundEscrow.errorConflictCannotFund');
    }
    if (serverMsg?.includes('Provider user mapping not found')) {
      return t('workspace.payments.fundEscrow.errorConflictProviderMapping');
    }
    return t('workspace.payments.fundEscrow.errorConflict');
  }

  return t('workspace.payments.fundEscrow.error');
}

function EscrowFundingPanel({
  plan,
  projectId,
  t,
}: {
  plan: PaymentPlanDto;
  projectId: string;
  t: (key: string) => string;
}) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [bankIntent, setBankIntent] = useState<FundingIntentDto | null>(null);

  const canFund = Boolean(pickFirstEscrowItemIdNeedingFunding(plan.items ?? []));
  if (!canFund) {
    return null;
  }

  const fundMutation = useMutation({
    mutationFn: (paymentMethod: (typeof PaymentMethod)[keyof typeof PaymentMethod]) =>
      paymentsApi.createFundingIntent(plan.id, {
        paymentMethod,
        returnUrl: `${window.location.origin}${location.pathname}${location.search}`,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', projectId] });
      queryClient.invalidateQueries({ queryKey: ['refund-requests', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      if (data.redirectUrl) {
        window.location.assign(data.redirectUrl);
      } else if (data.bankTransferInstructions) {
        setBankIntent(data);
      }
    },
  });

  const instr = bankIntent?.bankTransferInstructions;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('workspace.payments.fundEscrow.title')}</CardTitle>
          <CardAction />
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="default"
            disabled={fundMutation.isPending}
            onClick={() => fundMutation.mutate(PaymentMethod.CARD)}
          >
            {t('workspace.payments.fundEscrow.payCard')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={fundMutation.isPending}
            onClick={() => fundMutation.mutate(PaymentMethod.BANK_TRANSFER)}
          >
            {t('workspace.payments.fundEscrow.payBank')}
          </Button>
          {fundMutation.isError && (
            <p className="w-full text-sm text-destructive" role="alert">
              {escrowFundingErrorMessage(fundMutation.error, t)}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!instr} onOpenChange={(open) => !open && setBankIntent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.fundEscrow.bankDialogTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.fundEscrow.bankDialogDescription')}</DialogDescription>
          </DialogHeader>
          {instr && (
            <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
              <span className="text-muted-foreground">{t('workspace.payments.bankInfo.iban')}:</span>
              <span className="font-mono text-xs">{instr.iban}</span>
              <span className="text-muted-foreground">{t('workspace.payments.fundEscrow.bic')}:</span>
              <span className="font-mono text-xs">{instr.bic}</span>
              <span className="text-muted-foreground">{t('workspace.payments.fundEscrow.reference')}:</span>
              <span className="font-mono text-xs">{instr.reference}</span>
              <span className="col-span-2 text-xs text-muted-foreground pt-1">
                {t('workspace.payments.fundEscrow.referenceHint')}
              </span>
              <span className="text-muted-foreground">{t('workspace.payments.bankInfo.amount')}:</span>
              <span className="font-medium">{formatAmount(instr.amount, instr.currency)}</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankIntent(null)}>
              {t('workspace.payments.fundEscrow.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PlanHeader({ plan, t }: { plan: PaymentPlanDto; t: (key: string) => string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.payments.title')}</CardTitle>
        <CardAction>
          <Badge variant={planStatusVariant[plan.status]}>
            {t(`workspace.payments.planStatus.${plan.status}`)}
          </Badge>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.totalAmount')}:</span>{' '}
            <strong>{formatAmount(plan.totalAmountGross, plan.currency)}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.paymentMode')}:</span>{' '}
            <Badge variant={modeVariant[plan.defaultPaymentMode]} className="ml-1">
              {t(`workspace.payments.mode.${plan.defaultPaymentMode}`)}
            </Badge>
          </div>
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.platformFee')}:</span>{' '}
            <strong>{(plan.platformFeeRate * 100).toFixed(1)} %</strong>
          </div>
          {plan.platformFixedFee > 0 && (
            <div>
              <span className="text-muted-foreground">{t('workspace.payments.platformFixedFee')}:</span>{' '}
              <strong>{formatAmount(plan.platformFixedFee, plan.currency)}</strong>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.vatRateLabel')}:</span>{' '}
            <strong>{(plan.vatRate * 100).toFixed(1)} %</strong>
          </div>
          {plan.escrowAccount && (
            <div>
              <span className="text-muted-foreground">{t('workspace.payments.escrowBalanceTitle')}:</span>{' '}
              <strong>{formatAmount(plan.escrowAccount.balanceTotal, plan.currency)}</strong>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ItemCard({
  item,
  isClient,
  isSupplierOrStaff,
  firstDirectEligibleId,
  projectId,
  planCurrency,
  refundRequests,
  onApprove,
  onRequestChanges,
  reportMutation,
  confirmMutation,
  declareOffPlatformMutation,
  confirmOffPlatformMutation,
  rejectOffPlatformMutation,
  t,
}: {
  item: PaymentPlanItemDto;
  isClient: boolean;
  isSupplierOrStaff: boolean;
  firstDirectEligibleId: string | null;
  projectId: string;
  planCurrency: Currency;
  refundRequests: RefundRequestDto[];
  onApprove: (id: string) => void;
  onRequestChanges: (id: string) => void;
  reportMutation: UseMutationResult<
    PaymentConfirmationDto,
    Error,
    { itemId: string; dto: ConfirmDirectPaymentDto }
  >;
  confirmMutation: UseMutationResult<PaymentConfirmationDto, Error, string>;
  declareOffPlatformMutation: UseMutationResult<
    OffPlatformPaymentDto,
    Error,
    { itemId: string; dto: DeclareOffPlatformPaymentDto }
  >;
  confirmOffPlatformMutation: UseMutationResult<OffPlatformPaymentDto, Error, string>;
  rejectOffPlatformMutation: UseMutationResult<
    OffPlatformPaymentDto,
    Error,
    { id: string; reason?: string }
  >;
  t: (key: string) => string;
}) {
  const canAct = isClient && item.status === PaymentPlanItemStatusConst.COMPLETED_PENDING_APPROVAL;
  const [reportOpen, setReportOpen] = useState(false);
  const [amount, setAmount] = useState(String(item.amountGross));
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [offDialogOpen, setOffDialogOpen] = useState(false);
  const [offAmount, setOffAmount] = useState(String(item.amountGross));
  const [offPaidAt, setOffPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [offPdfFile, setOffPdfFile] = useState<File | null>(null);
  const [rejectOffId, setRejectOffId] = useState<string | null>(null);
  const [rejectOffReason, setRejectOffReason] = useState('');
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeDesc, setDisputeDesc] = useState('');
  const [tmDisputeReason, setTmDisputeReason] = useState<string>(DisputeReasonCodes.BILLING_DISAGREEMENT);
  const [milestoneDisputeOpen, setMilestoneDisputeOpen] = useState(false);
  const [milestoneDisputeReason, setMilestoneDisputeReason] = useState<string>(DisputeReasonCodes.QUALITY_ISSUE);
  const [milestoneDisputeDesc, setMilestoneDisputeDesc] = useState('');
  const [milestoneEvidenceFiles, setMilestoneEvidenceFiles] = useState<File[]>([]);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundCategory, setRefundCategory] = useState<string>(RefundRequestCategory.PARTIAL);
  const [refundAmountStr, setRefundAmountStr] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const queryClient = useQueryClient();
  const tmQueryEnabled = item.type === PaymentPlanItemType.T_M_PERIOD;
  const { data: tmInvoice } = useQuery({
    queryKey: ['tm-invoice', projectId, item.id],
    queryFn: () => paymentsApi.getTmInvoiceDetailForPlanItem(projectId, item.id),
    enabled: tmQueryEnabled,
  });

  const approveTmInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string) => paymentsApi.approveInvoiceForPayment(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tm-invoice', projectId, item.id] });
      queryClient.invalidateQueries({ queryKey: ['payment-plans', projectId] });
      toast.success(t('workspace.payments.tmInvoice.approveSuccess'));
    },
  });

  const disputeTmMutation = useMutation({
    mutationFn: () =>
      paymentsApi.raiseDispute(item.id, {
        reasonCode: tmDisputeReason as DisputeReasonCode,
        description: disputeDesc.trim(),
        disputedAmount: tmInvoice?.amount ?? item.amountGross,
        invoiceId: tmInvoice?.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tm-invoice', projectId, item.id] });
      queryClient.invalidateQueries({ queryKey: ['payment-plans', projectId] });
      setDisputeOpen(false);
      setDisputeDesc('');
      setTmDisputeReason(DisputeReasonCodes.BILLING_DISAGREEMENT);
      toast.success(t('workspace.payments.tmInvoice.disputeSuccess'));
    },
    onError: () => {
      toast.error(t('workspace.payments.tmInvoice.disputeError'));
    },
  });

  const MIN_DISPUTE_DESC = 20;

  const milestoneDisputeMutation = useMutation({
    mutationFn: async () => {
      const urls: string[] = [];
      for (const file of milestoneEvidenceFiles) {
        const uploaded = await projectsApi.uploadWorkspaceFile(
          projectId,
          file,
          ProjectWorkspaceFileCategory.OTHER,
        );
        urls.push(buildPaymentProofDocumentUrl(uploaded.id));
      }
      return paymentsApi.raiseDispute(item.id, {
        reasonCode: milestoneDisputeReason as DisputeReasonCode,
        description: milestoneDisputeDesc.trim(),
        disputedAmount: item.amountGross,
        attachmentUrls: urls.length ? urls : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', projectId] });
      setMilestoneDisputeOpen(false);
      setMilestoneDisputeDesc('');
      setMilestoneEvidenceFiles([]);
      setMilestoneDisputeReason(DisputeReasonCodes.QUALITY_ISSUE);
      toast.success(t('workspace.payments.milestoneDispute.success'));
    },
    onError: () => {
      toast.error(t('workspace.payments.milestoneDispute.error'));
    },
  });

  useEffect(() => {
    if (reportOpen) {
      setAmount(String(item.amountGross));
      setPdfFile(null);
    }
  }, [reportOpen, item.amountGross]);

  useEffect(() => {
    if (offDialogOpen) {
      setOffAmount(String(item.amountGross));
      setOffPaidAt(new Date().toISOString().slice(0, 10));
      setOffPdfFile(null);
    }
  }, [offDialogOpen, item.amountGross]);

  const openDispute = item.disputes?.find(
    (d) => d.status === DisputeStatus.OPEN || d.status === DisputeStatus.UNDER_REVIEW,
  );
  const resolvedDispute = item.disputes?.find(
    (d) =>
      d.status === DisputeStatus.RESOLVED_CLIENT_FAVOUR ||
      d.status === DisputeStatus.RESOLVED_PROVIDER_FAVOUR ||
      d.status === DisputeStatus.COMPROMISE,
  );

  const itemEscrowRefunds = refundRequests.filter((r) => r.paymentPlanItemId === item.id);
  const hasOpenRefundRequest = itemEscrowRefunds.some((r) =>
    (
      [
        RefundRequestStatus.SUBMITTED,
        RefundRequestStatus.APPROVED,
        RefundRequestStatus.PROCESSING,
      ] as string[]
    ).includes(r.status),
  );
  const canStartRefundRequest =
    isClient &&
    item.paymentMode === PaymentModeConst.ESCROW &&
    (item.fundingStatus === FundingStatus.FULLY_FUNDED ||
      item.fundingStatus === FundingStatus.PARTIALLY_FUNDED) &&
    !openDispute &&
    !hasOpenRefundRequest;

  const REFUND_REASON_MIN = 20;

  const refundMutation = useMutation({
    mutationFn: () =>
      paymentsApi.createRefundRequest(projectId, {
        paymentPlanItemId: item.id,
        category: refundCategory as typeof RefundRequestCategory.PRE_WORK | typeof RefundRequestCategory.PARTIAL,
        requestedAmount: Number(refundAmountStr.replace(',', '.')),
        reason: refundReason.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refund-requests', projectId] });
      queryClient.invalidateQueries({ queryKey: ['payment-plans', projectId] });
      setRefundOpen(false);
      setRefundReason('');
      setRefundAmountStr('');
      setRefundCategory(RefundRequestCategory.PARTIAL);
      toast.success(t('workspace.payments.refundRequest.success'));
    },
    onError: () => {
      toast.error(t('workspace.payments.refundRequest.error'));
    },
  });

  useEffect(() => {
    if (refundOpen) {
      setRefundAmountStr(String(item.escrowTargetAmount));
      setRefundReason('');
      setRefundCategory(
        item.status === PaymentPlanItemStatusConst.PENDING && item.fundingStatus === FundingStatus.FULLY_FUNDED
          ? RefundRequestCategory.PRE_WORK
          : RefundRequestCategory.PARTIAL,
      );
    }
  }, [refundOpen, item.escrowTargetAmount, item.status, item.fundingStatus]);

  const pending = item.paymentConfirmations?.find((c) => c.status === PaymentConfirmationStatus.PENDING);
  const pendingOff = item.offPlatformPayments?.find((o) => o.status === OffPlatformPaymentStatus.PENDING);
  const hasConfirmedOff = item.offPlatformPayments?.some((o) => o.status === OffPlatformPaymentStatus.CONFIRMED);
  const canShowReport =
    isClient &&
    item.paymentMode === PaymentModeConst.DIRECT &&
    item.status === PaymentPlanItemStatusConst.PENDING &&
    item.id === firstDirectEligibleId &&
    !pending &&
    (item.type !== PaymentPlanItemType.T_M_PERIOD || tmInvoice?.status === InvoiceStatus.DUE);

  const canShowConfirm = Boolean(isSupplierOrStaff && pending);

  const canShowOffPlatformDeclare =
    isSupplierOrStaff &&
    item.paymentMode === PaymentModeConst.OFF_PLATFORM &&
    item.status === PaymentPlanItemStatusConst.PENDING &&
    !pendingOff &&
    !hasConfirmedOff;

  const canShowOffPlatformReview = Boolean(isSupplierOrStaff && pendingOff);

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <span className="text-xs text-muted-foreground mr-2">#{item.orderIndex + 1}</span>
          <span className="font-medium">{item.label}</span>
        </div>
        <Badge variant={itemStatusVariant[item.status]}>
          {t(`workspace.payments.itemStatus.${item.status}`)}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-3">
        <div>
          <span className="text-muted-foreground">{t('workspace.payments.amount')}:</span>{' '}
          <strong>{formatAmount(item.amountGross, planCurrency)}</strong>
        </div>
        {item.dueDate && (
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.dueDate')}:</span>{' '}
            {formatDate(item.dueDate)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm mb-3 rounded-md border border-border/60 bg-muted/30 p-2">
        <span className="text-muted-foreground">{t('workspace.payments.breakdown.net')}:</span>
        <span className="tabular-nums font-medium">{formatAmount(item.amountNet, planCurrency)}</span>
        <span className="text-muted-foreground">{t('workspace.payments.breakdown.vat')}:</span>
        <span className="tabular-nums font-medium">{formatAmount(item.vatAmount, planCurrency)}</span>
        <span className="text-muted-foreground">{t('workspace.payments.breakdown.platformFee')}:</span>
        <span className="tabular-nums font-medium">{formatAmount(item.platformFeeAmount, planCurrency)}</span>
      </div>

      <div className='grid grid-cols-[max-content_1fr] gap-2'>
        <span className="shrink-1 text-muted-foreground">{t('workspace.payments.paymentMode')}:</span>
        <Badge className=' w-fit' variant={modeVariant[item.paymentMode]}>
          {t(`workspace.payments.mode.${item.paymentMode}`)}
        </Badge>
        <span className="shrink-1 text-muted-foreground">{t('workspace.payments.fundingStatusTitle')}:</span>
        <Badge className=' w-fit' variant={fundingStatusVariant[item.fundingStatus]}>
          {t(`workspace.payments.fundingStatus.${item.fundingStatus}`)}
        </Badge>
        <span className="shrink-1 text-muted-foreground">{t('workspace.payments.payoutStatusTitle')}:</span>
        <Badge className=' w-fit' variant={payoutStatusVariant[item.payoutStatus]}>
          {t(`workspace.payments.payoutStatus.${item.payoutStatus}`)}
        </Badge>
      </div>

      {isClient && item.paymentMode === PaymentModeConst.ESCROW && itemEscrowRefunds.length > 0 && (
        <div className="pt-3 mt-3 border-t text-sm space-y-2">
          <p className="font-medium">{t('workspace.payments.refundRequest.historyTitle')}</p>
          {itemEscrowRefunds.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">{t(`workspace.payments.refundRequest.status.${r.status}`)}</Badge>
              <span>{formatAmount(r.requestedAmount, planCurrency)}</span>
              <span className="text-muted-foreground truncate max-w-[220px]" title={r.reason}>
                {r.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      {canStartRefundRequest && (
        <div className="pt-3 mt-3 border-t">
          <Button size="sm" variant="outline" onClick={() => setRefundOpen(true)}>
            {t('workspace.payments.refundRequest.open')}
          </Button>
        </div>
      )}

      {openDispute && (
        <div className="pt-3 mt-3 border-t text-sm rounded-md border border-brand-primary/40 bg-brand-primary/10 p-3">
          <p className="font-medium text-amber-950 dark:text-amber-100">
            {t('workspace.payments.disputeResolution.openTitle')}
          </p>
          <p className="text-muted-foreground mt-1">{t('workspace.payments.disputeResolution.openHint')}</p>
        </div>
      )}

      {resolvedDispute && (
        <div className="pt-3 mt-3 border-t space-y-2 text-sm rounded-md border border-border/60 bg-muted/20 p-3">
          <h4 className="text-sm font-medium">{t('workspace.payments.disputeResolution.title')}</h4>
          <div>
            <span className="text-muted-foreground">{t('workspace.payments.disputeResolution.outcome')}:</span>{' '}
            <strong>{t(`workspace.payments.disputeResolution.status.${resolvedDispute.status}`)}</strong>
          </div>
          {(resolvedDispute.refundAmount != null && resolvedDispute.refundAmount > 0) ||
          (resolvedDispute.releaseAmount != null && resolvedDispute.releaseAmount > 0) ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {resolvedDispute.refundAmount != null && resolvedDispute.refundAmount > 0 && (
                <div>
                  <span className="text-muted-foreground">{t('workspace.payments.disputeResolution.refund')}:</span>{' '}
                  <strong>{formatAmount(resolvedDispute.refundAmount, planCurrency)}</strong>
                </div>
              )}
              {resolvedDispute.releaseAmount != null && resolvedDispute.releaseAmount > 0 && (
                <div>
                  <span className="text-muted-foreground">{t('workspace.payments.disputeResolution.release')}:</span>{' '}
                  <strong>{formatAmount(resolvedDispute.releaseAmount, planCurrency)}</strong>
                </div>
              )}
            </div>
          ) : null}
          {resolvedDispute.resolutionSummary ? (
            <div>
              <p className="text-muted-foreground text-xs mb-1">{t('workspace.payments.disputeResolution.summary')}</p>
              <p className="whitespace-pre-wrap rounded-md bg-background border p-2 text-sm">{resolvedDispute.resolutionSummary}</p>
            </div>
          ) : null}
        </div>
      )}

      {tmQueryEnabled && (
        <div className="pt-3 mt-3 border-t space-y-3 text-sm">
          <h4 className="text-sm font-medium">{t('workspace.payments.tmInvoice.title')}</h4>
          {!tmInvoice ? (
            <p className="text-muted-foreground">{t('workspace.payments.tmInvoice.noInvoice')}</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <div>
                  <span className="text-muted-foreground">{t('workspace.payments.tmInvoice.invoiceNumber')}:</span>{' '}
                  <span className="font-mono">{tmInvoice.invoiceNumber}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('workspace.payments.tmInvoice.status')}:</span>{' '}
                  <Badge variant="secondary">
                    {t(`workspace.payments.invoiceStatus.${tmInvoice.status}`)}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('workspace.payments.amount')}:</span>{' '}
                  <strong>{formatAmount(tmInvoice.amount, tmInvoice.currency)}</strong>
                </div>
              </div>
              {tmInvoice.timesheetLines.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-2">{t('workspace.payments.tmInvoice.linesTitle')}</p>
                  <div className="rounded-md border overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">{t('workspace.payments.tmInvoice.workDate')}</th>
                          <th className="text-right p-2">{t('workspace.payments.tmInvoice.hours')}</th>
                          <th className="text-left p-2">{t('workspace.payments.tmInvoice.description')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tmInvoice.timesheetLines.map((row) => (
                          <tr key={row.timeEntryId} className="border-t">
                            <td className="p-2 whitespace-nowrap">{formatDate(row.workDate)}</td>
                            <td className="p-2 text-right tabular-nums">{row.hours}</td>
                            <td className="p-2">{row.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {isClient && tmInvoice.status === InvoiceStatus.DRAFT && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    disabled={approveTmInvoiceMutation.isPending}
                    onClick={() => approveTmInvoiceMutation.mutate(tmInvoice.id)}
                  >
                    {t('workspace.payments.tmInvoice.approve')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDisputeOpen(true)}>
                    {t('workspace.payments.tmInvoice.dispute')}
                  </Button>
                </div>
              )}
              {isClient && tmInvoice.status === InvoiceStatus.DUE && (
                <Button size="sm" variant="outline" onClick={() => setDisputeOpen(true)}>
                  {t('workspace.payments.tmInvoice.dispute')}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {item.spaydData && (
        <div className="pt-3 mt-3 border-t space-y-3">
          <h4 className="text-sm font-medium">{t('workspace.payments.bankInfo.title')}</h4>
          <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
            <span className="text-muted-foreground">{t('workspace.payments.bankInfo.iban')}:</span>
            <span className="font-mono text-xs">{item.spaydData.iban}</span>
            {item.spaydData.recipientName && (
              <>
                <span className="text-muted-foreground">{t('workspace.payments.bankInfo.recipient')}:</span>
                <span>{item.spaydData.recipientName}</span>
              </>
            )}
            <span className="text-muted-foreground">{t('workspace.payments.bankInfo.amount')}:</span>
            <span className="font-medium">{formatAmount(item.spaydData.amount, item.spaydData.currency)}</span>
            {item.spaydData.variableSymbol && (
              <>
                <span className="text-muted-foreground">{t('workspace.payments.bankInfo.variableSymbol')}:</span>
                <span className="font-mono">{item.spaydData.variableSymbol}</span>
              </>
            )}
          </div>
          <div className="flex flex-col items-center gap-2 pt-2">
            <QRCodeSVG value={item.spaydData.spaydString} size={160} level="M" />
            <span className="text-xs text-muted-foreground">{t('workspace.payments.bankInfo.qrLabel')}</span>
          </div>
        </div>
      )}

      {pending && (
        <div className="pt-3 mt-3 border-t space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning">{t(`workspace.payments.direct.confirmationStatus.${pending.status}`)}</Badge>
            <span className="text-muted-foreground">
              {formatAmount(pending.amount, pending.currency)} · {formatDate(pending.paidAt)}
            </span>
          </div>
          {canShowConfirm && (
            <Button
              size="sm"
              variant="default"
              disabled={confirmMutation.isPending}
              onClick={() => confirmMutation.mutate(pending.id)}
            >
              {t('workspace.payments.direct.confirmReceipt')}
            </Button>
          )}
          {!canShowConfirm && isClient && (
            <p className="text-muted-foreground">{t('workspace.payments.direct.awaitingSupplier')}</p>
          )}
        </div>
      )}

      {canShowReport && (
        <div className="pt-3 mt-3 border-t">
          <Button size="sm" variant="outline" onClick={() => setReportOpen(true)}>
            {t('workspace.payments.direct.reportTitle')}
          </Button>
        </div>
      )}

      {pendingOff && (
        <div className="pt-3 mt-3 border-t space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning">{t(`workspace.payments.offPlatform.status.${pendingOff.status}`)}</Badge>
            <span className="text-muted-foreground">
              {formatAmount(pendingOff.amount, pendingOff.currency)} · {formatDate(pendingOff.paidAt)}
            </span>
          </div>
          {canShowOffPlatformReview && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="default"
                disabled={confirmOffPlatformMutation.isPending}
                onClick={() => confirmOffPlatformMutation.mutate(pendingOff.id)}
              >
                {t('workspace.payments.offPlatform.confirm')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejectOffId(pendingOff.id)}>
                {t('workspace.payments.offPlatform.reject')}
              </Button>
            </div>
          )}
          {!canShowOffPlatformReview && isClient && (
            <p className="text-muted-foreground">{t('workspace.payments.offPlatform.awaitingReview')}</p>
          )}
        </div>
      )}

      {canShowOffPlatformDeclare && (
        <div className="pt-3 mt-3 border-t">
          <Button size="sm" variant="outline" onClick={() => setOffDialogOpen(true)}>
            {t('workspace.payments.offPlatform.declareTitle')}
          </Button>
        </div>
      )}

      {canAct && (
        <div className="flex flex-wrap gap-2 pt-3 border-t">
          <Button size="sm" variant="success" onClick={() => onApprove(item.id)}>
            {t('workspace.payments.approve')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRequestChanges(item.id)}>
            {t('workspace.payments.requestChanges')}
          </Button>
          {item.type !== PaymentPlanItemType.T_M_PERIOD && (
            <Button size="sm" variant="destructive" onClick={() => setMilestoneDisputeOpen(true)}>
              {t('workspace.payments.milestoneDispute.open')}
            </Button>
          )}
        </div>
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.direct.reportTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.direct.reportDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor={`direct-amt-${item.id}`}>{t('workspace.payments.direct.amountLabel')}</Label>
              <Input
                id={`direct-amt-${item.id}`}
                type="number"
                min={0.01}
                step={0.01}
                className="mt-1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`direct-pdf-${item.id}`}>{t('workspace.payments.direct.pdfLabel')}</Label>
              <Input
                id={`direct-pdf-${item.id}`}
                type="file"
                accept="application/pdf"
                className="mt-1"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {reportMutation.isError && (
              <p className="text-sm text-destructive" role="alert">
                {t('workspace.payments.direct.reportError')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button
              disabled={
                !pdfFile ||
                reportMutation.isPending ||
                Number(amount) <= 0 ||
                Number(amount) > item.amountGross
              }
              onClick={async () => {
                if (!pdfFile) return;
                try {
                  const uploaded = await projectsApi.uploadWorkspaceFile(
                    projectId,
                    pdfFile,
                    ProjectWorkspaceFileCategory.OTHER,
                  );
                  await reportMutation.mutateAsync({
                    itemId: item.id,
                    dto: {
                      amount: Number(amount),
                      currency: planCurrency,
                      paidAt: new Date().toISOString().slice(0, 10),
                      workspaceFileId: uploaded.id,
                    },
                  });
                  setReportOpen(false);
                  toast.success(t('workspace.payments.direct.reportSent'));
                } catch {
                  toast.error(t('workspace.payments.direct.reportError'));
                }
              }}
            >
              {t('workspace.payments.direct.submitReport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={offDialogOpen} onOpenChange={setOffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.offPlatform.declareTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.offPlatform.declareDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor={`off-amt-${item.id}`}>{t('workspace.payments.offPlatform.amountLabel')}</Label>
              <Input
                id={`off-amt-${item.id}`}
                type="number"
                min={0.01}
                step={0.01}
                className="mt-1"
                value={offAmount}
                onChange={(e) => setOffAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`off-date-${item.id}`}>{t('workspace.payments.offPlatform.dateLabel')}</Label>
              <Input
                id={`off-date-${item.id}`}
                type="date"
                className="mt-1"
                value={offPaidAt}
                onChange={(e) => setOffPaidAt(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`off-pdf-${item.id}`}>{t('workspace.payments.offPlatform.pdfLabel')}</Label>
              <Input
                id={`off-pdf-${item.id}`}
                type="file"
                accept="application/pdf"
                className="mt-1"
                onChange={(e) => setOffPdfFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {declareOffPlatformMutation.isError && (
              <p className="text-sm text-destructive" role="alert">
                {t('workspace.payments.offPlatform.declareError')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOffDialogOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button
              disabled={
                !offPdfFile ||
                declareOffPlatformMutation.isPending ||
                Number(offAmount) <= 0 ||
                Number(offAmount) > item.amountGross
              }
              onClick={async () => {
                if (!offPdfFile) return;
                try {
                  const uploaded = await projectsApi.uploadWorkspaceFile(
                    projectId,
                    offPdfFile,
                    ProjectWorkspaceFileCategory.OTHER,
                  );
                  await declareOffPlatformMutation.mutateAsync({
                    itemId: item.id,
                    dto: {
                      amount: Number(offAmount),
                      currency: planCurrency,
                      paidAt: offPaidAt,
                      workspaceFileId: uploaded.id,
                    },
                  });
                  setOffDialogOpen(false);
                  toast.success(t('workspace.payments.offPlatform.declareSent'));
                } catch {
                  toast.error(t('workspace.payments.offPlatform.declareError'));
                }
              }}
            >
              {t('workspace.payments.offPlatform.submitDeclare')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectOffId} onOpenChange={(open) => !open && setRejectOffId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.offPlatform.rejectDialogTitle')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.offPlatform.rejectDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor={`off-reject-${item.id}`}>{t('workspace.payments.offPlatform.rejectReasonLabel')}</Label>
            <textarea
              id={`off-reject-${item.id}`}
              className="mt-1 w-full min-h-[80px] rounded-md border bg-background px-3 py-2 text-sm"
              value={rejectOffReason}
              onChange={(e) => setRejectOffReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOffId(null)}>
              {t('generic.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={rejectOffPlatformMutation.isPending || !rejectOffId}
              onClick={() =>
                rejectOffId &&
                rejectOffPlatformMutation.mutate(
                  { id: rejectOffId, reason: rejectOffReason.trim() || undefined },
                  {
                    onSuccess: () => {
                      setRejectOffId(null);
                      setRejectOffReason('');
                    },
                  },
                )
              }
            >
              {t('workspace.payments.offPlatform.submitReject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={disputeOpen}
        onOpenChange={(open) => {
          setDisputeOpen(open);
          if (!open) {
            setDisputeDesc('');
            setTmDisputeReason(DisputeReasonCodes.BILLING_DISAGREEMENT);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.tmInvoice.disputeTitle')}</DialogTitle>
            <DialogDescription>
              {t('workspace.payments.tmInvoice.disputeDescription', { min: MIN_DISPUTE_DESC })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('workspace.payments.tmInvoice.disputeReasonLabel')}</Label>
              <Select
                value={tmDisputeReason}
                onValueChange={(v) => setTmDisputeReason(v)}
              >
                <SelectTrigger className="mt-1 w-full max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DisputeReasonCodes.BILLING_DISAGREEMENT}>
                    {t('workspace.payments.tmInvoice.disputeReasonBilling')}
                  </SelectItem>
                  <SelectItem value={DisputeReasonCodes.OTHER}>{t('workspace.payments.tmInvoice.disputeReasonOther')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <textarea
              className="w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm"
              value={disputeDesc}
              onChange={(e) => setDisputeDesc(e.target.value)}
              placeholder={t('workspace.payments.tmInvoice.disputePlaceholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('workspace.payments.milestoneDispute.minCharsHint', { min: MIN_DISPUTE_DESC })}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={
                disputeDesc.trim().length < MIN_DISPUTE_DESC || disputeTmMutation.isPending || !tmInvoice
              }
              onClick={() => disputeTmMutation.mutate()}
            >
              {t('workspace.payments.tmInvoice.submitDispute')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={milestoneDisputeOpen}
        onOpenChange={(open) => {
          setMilestoneDisputeOpen(open);
          if (!open) {
            setMilestoneDisputeDesc('');
            setMilestoneEvidenceFiles([]);
            setMilestoneDisputeReason(DisputeReasonCodes.QUALITY_ISSUE);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.milestoneDispute.title')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.milestoneDispute.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('workspace.payments.milestoneDispute.reasonLabel')}</Label>
              <Select
                value={milestoneDisputeReason}
                onValueChange={(v) => setMilestoneDisputeReason(v)}
              >
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DisputeReasonCodes.QUALITY_ISSUE}>
                    {t('workspace.payments.milestoneDispute.reason.QUALITY_ISSUE')}
                  </SelectItem>
                  <SelectItem value={DisputeReasonCodes.DELAY}>
                    {t('workspace.payments.milestoneDispute.reason.DELAY')}
                  </SelectItem>
                  <SelectItem value={DisputeReasonCodes.SCOPE_DISAGREEMENT}>
                    {t('workspace.payments.milestoneDispute.reason.SCOPE_DISAGREEMENT')}
                  </SelectItem>
                  <SelectItem value={DisputeReasonCodes.OTHER}>{t('workspace.payments.milestoneDispute.reason.OTHER')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`milestone-dispute-desc-${item.id}`}>{t('workspace.payments.milestoneDispute.problemLabel')}</Label>
              <textarea
                id={`milestone-dispute-desc-${item.id}`}
                className="mt-1 w-full min-h-[120px] rounded-md border bg-background px-3 py-2 text-sm"
                value={milestoneDisputeDesc}
                onChange={(e) => setMilestoneDisputeDesc(e.target.value)}
                placeholder={t('workspace.payments.milestoneDispute.problemPlaceholder')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('workspace.payments.milestoneDispute.minCharsHint', { min: MIN_DISPUTE_DESC })}
              </p>
            </div>
            <div>
              <Label htmlFor={`milestone-dispute-files-${item.id}`}>{t('workspace.payments.milestoneDispute.attachmentsLabel')}</Label>
              <Input
                id={`milestone-dispute-files-${item.id}`}
                type="file"
                multiple
                className="mt-1"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx,.xlsm,.ppt,.pptx,.dwg"
                onChange={(e) => setMilestoneEvidenceFiles(Array.from(e.target.files ?? []))}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('workspace.payments.milestoneDispute.attachmentsHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMilestoneDisputeOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={
                milestoneDisputeDesc.trim().length < MIN_DISPUTE_DESC ||
                milestoneDisputeMutation.isPending
              }
              onClick={() => milestoneDisputeMutation.mutate()}
            >
              {t('workspace.payments.milestoneDispute.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={refundOpen}
        onOpenChange={(open) => {
          setRefundOpen(open);
          if (!open) {
            setRefundReason('');
            setRefundAmountStr('');
            setRefundCategory(RefundRequestCategory.PARTIAL);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.refundRequest.title')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.refundRequest.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('workspace.payments.refundRequest.categoryLabel')}</Label>
              <Select value={refundCategory} onValueChange={setRefundCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RefundRequestCategory.PRE_WORK}>
                    {t('workspace.payments.refundRequest.category.PRE_WORK')}
                  </SelectItem>
                  <SelectItem value={RefundRequestCategory.PARTIAL}>
                    {t('workspace.payments.refundRequest.category.PARTIAL')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`refund-amt-${item.id}`}>{t('workspace.payments.refundRequest.amountLabel')}</Label>
              <Input
                id={`refund-amt-${item.id}`}
                type="number"
                min={0.01}
                step={0.01}
                className="mt-1"
                value={refundAmountStr}
                onChange={(e) => setRefundAmountStr(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`refund-reason-${item.id}`}>{t('workspace.payments.refundRequest.reasonLabel')}</Label>
              <textarea
                id={`refund-reason-${item.id}`}
                className="mt-1 w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t('workspace.payments.refundRequest.reasonPlaceholder')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('workspace.payments.refundRequest.minChars', { min: REFUND_REASON_MIN })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button
              disabled={
                refundReason.trim().length < REFUND_REASON_MIN ||
                refundMutation.isPending ||
                Number(refundAmountStr.replace(',', '.')) <= 0
              }
              onClick={() => refundMutation.mutate()}
            >
              {t('workspace.payments.refundRequest.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProjectPaymentsPage() {
  const { project, inquiry, offer } = useOutletContext<{
    project: ProjectDto;
    inquiry: InquiryDto;
    offer: OfferDto;
  }>();
  const { t } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectPaymentsPage' });
  const queryClient = useQueryClient();

  const isClient = user?.account?.id === inquiry.accountId;
  const isSupplierOrStaff =
    user?.account?.id === offer.accountId ||
    user?.systemRole === 'SUPERADMIN' ||
    user?.systemRole === 'SUPPORT';

  const { data: plans, isLoading } = useQuery({
    queryKey: ['payment-plans', project.id],
    queryFn: () => paymentsApi.getByProjectId(project.id),
  });

  const { data: refundRequests = [] } = useQuery({
    queryKey: ['refund-requests', project.id],
    queryFn: () => paymentsApi.listRefundRequestsByProject(project.id),
    enabled: isClient,
  });

  const approveMutation = useMutation({
    mutationFn: (itemId: string) => paymentsApi.approveItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });

  const [changesItemId, setChangesItemId] = useState<string | null>(null);
  const [changesReason, setChangesReason] = useState('');

  const requestChangesMutation = useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      paymentsApi.requestChanges(itemId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      setChangesItemId(null);
      setChangesReason('');
    },
  });

  const reportDirectMutation = useMutation({
    mutationFn: ({ itemId, dto }: { itemId: string; dto: ConfirmDirectPaymentDto }) =>
      paymentsApi.reportDirectPayment(itemId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });

  const confirmDirectMutation = useMutation({
    mutationFn: (confirmationId: string) => paymentsApi.confirmDirectPaymentReceipt(confirmationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      toast.success(t('workspace.payments.direct.confirmReceiptSuccess'));
    },
  });

  const declareOffPlatformMutation = useMutation({
    mutationFn: ({ itemId, dto }: { itemId: string; dto: DeclareOffPlatformPaymentDto }) =>
      paymentsApi.declareOffPlatformPayment(itemId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });

  const confirmOffPlatformMutation = useMutation({
    mutationFn: (id: string) => paymentsApi.confirmOffPlatformPayment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      toast.success(t('workspace.payments.offPlatform.confirmSuccess'));
    },
  });

  const rejectOffPlatformMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      paymentsApi.rejectOffPlatformPayment(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans', project.id] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      toast.success(t('workspace.payments.offPlatform.rejectSuccess'));
    },
  });

  if (isLoading) return <Loading />;

  const plan = plans?.[0];

  if (!plan) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        {t('workspace.payments.empty')}
      </div>
    );
  }

  const sortedItems = [...plan.items].sort((a, b) => a.orderIndex - b.orderIndex);

  const confirmationPicks = plan.items.flatMap((it) =>
    (it.paymentConfirmations ?? []).map((c) => ({
      paymentPlanItemId: c.paymentPlanItemId,
      status: c.status,
    })),
  );
  const firstDirectEligibleId = pickFirstDirectItemIdAwaitingConfirmedReceipt(
    plan.items.map((it) => ({
      id: it.id,
      orderIndex: it.orderIndex,
      paymentMode: it.paymentMode,
    })),
    confirmationPicks,
  );

  return (
    <div className="space-y-6">
      <PlanHeader plan={plan} t={t} />

      {isClient && <EscrowFundingPanel plan={plan} projectId={project.id} t={t} />}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sortedItems.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            isClient={isClient}
            isSupplierOrStaff={isSupplierOrStaff}
            firstDirectEligibleId={firstDirectEligibleId}
            projectId={project.id}
            planCurrency={plan.currency}
            refundRequests={refundRequests}
            onApprove={(id) => approveMutation.mutate(id)}
            onRequestChanges={(id) => {
              setChangesItemId(id);
              setChangesReason('');
            }}
            reportMutation={reportDirectMutation}
            confirmMutation={confirmDirectMutation}
            declareOffPlatformMutation={declareOffPlatformMutation}
            confirmOffPlatformMutation={confirmOffPlatformMutation}
            rejectOffPlatformMutation={rejectOffPlatformMutation}
            t={t}
          />
        ))}
      </div>

      <Dialog open={!!changesItemId} onOpenChange={(open) => !open && setChangesItemId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.payments.requestChanges')}</DialogTitle>
            <DialogDescription>{t('workspace.payments.requestChangesReason')}</DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm"
            value={changesReason}
            onChange={(e) => setChangesReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangesItemId(null)}>
              {t('generic.cancel')}
            </Button>
            <Button
              disabled={!changesReason.trim() || requestChangesMutation.isPending}
              onClick={() => changesItemId && requestChangesMutation.mutate({ itemId: changesItemId, reason: changesReason })}
            >
              {t('generic.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

ProjectPaymentsPage.displayName = 'ProjectPaymentsPage';
